import { useState, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ScrollReveal } from '@/components/feature/PageLayout';

export default function ContactoPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9htif0h9dsfbuoi5k70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 8000);
      } else if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
        setFormError('No se pudo enviar el mensaje. Por favor, intenta de nuevo.');
        setFormStatus('error');
      } else {
        setFormError(serverMsg || 'Ocurrió un error. Por favor, intenta de nuevo.');
        setFormStatus('error');
      }
    } catch {
      setFormError('Error de conexión. Por favor, verifica tu internet e intenta de nuevo.');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[55vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20architectural%20interior%20with%20a%20large%20wooden%20table%20and%20elegant%20chairs%2C%20soft%20natural%20light%20from%20floor%20to%20ceiling%20windows%2C%20cream%20walls%2C%20polished%20concrete%20floor%2C%20minimalist%20yet%20warm%20atmosphere%2C%20pottery%20with%20dried%20branches%2C%20editorial%20photography%2C%20fine%20grain%2C%20cinematic%20depth%2C%20beige%20and%20taupe%20palette&width=1920&height=800&seq=contacto-hero&orientation=landscape"
              alt="Espacio arquitectónico cálido para conversación y mentoría"
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/55 to-foreground-950/75"></div>

          <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 pt-24 pb-16">
            <div className="reveal-on-scroll">
              <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-accent-400/40 text-accent-300 text-xs tracking-[0.15em]">
                08
              </div>
              <p className="text-accent-300 text-sm tracking-[0.2em] mb-4">CONTACTO</p>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-[0.02em] text-background-50 leading-[1.15] mb-6">
                Conversemos<br className="hidden sm:block" />con CALMA
              </h1>
              <p className="text-background-50/75 text-base md:text-lg leading-relaxed max-w-[560px]">
                A veces no necesitas un plan nuevo, sino alguien que crea en ti cuando tú dudas.
              </p>
            </div>
          </div>
        </section>

        {/* Contact section */}
        <ScrollReveal>
          <section className="relative py-20 md:py-28 lg:py-36 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20architectural%20interior%20with%20a%20large%20wooden%20table%20and%20elegant%20chairs%2C%20soft%20natural%20light%20from%20floor%20to%20ceiling%20windows%2C%20cream%20walls%2C%20polished%20concrete%20floor%2C%20minimalist%20yet%20warm%20atmosphere%2C%20pottery%20with%20dried%20branches%2C%20editorial%20photography%2C%20fine%20grain%2C%20cinematic%20depth%2C%20beige%20and%20taupe%20palette&width=1920&height=1400&seq=contacto-bg&orientation=landscape"
                alt="Espacio arquitectónico cálido"
                className="w-full h-full object-cover object-center"
                loading="lazy"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/85 via-foreground-950/70 to-foreground-950/85"></div>

            <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
              <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 reveal-on-scroll">
                {/* Left: Contact info */}
                <div className="lg:w-5/12">
                  <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.02em] text-background-50 leading-[1.2] mb-6">
                    Hablemos
                  </h2>
                  <p className="text-background-50/70 text-base md:text-lg leading-relaxed mb-10 max-w-[440px]">
                    Agenda una sesión inicial sin costo para descubrir cómo CALMA puede ayudarte a avanzar con mayor claridad e intención estratégica.
                  </p>

                  <blockquote className="border-l-2 border-accent-400/60 pl-6 py-1 mb-12">
                    <p className="font-heading text-xl md:text-2xl italic font-light text-background-50/80 leading-relaxed">
                      A veces no necesitas un plan nuevo, sino alguien que crea en ti cuando tú dudas.
                    </p>
                  </blockquote>

                  <div className="space-y-4">
                    <a
                      href="mailto:mentorias@erikalopezc.com"
                      className="flex items-center gap-3 text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-line text-lg text-accent-400"></i>
                      <span className="text-sm">mentorias@erikalopezc.com</span>
                    </a>
                    <a
                      href="https://wa.me/526646121065?text=Hola%2C%20Erika.%20Me%20gustar%C3%ADa%20conocer%20m%C3%A1s%20sobre%20la%20mentor%C3%ADa%20de%20reflexi%C3%B3n%20estrat%C3%A9gica%20y%20agendar%20una%20sesi%C3%B3n%20inicial."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-lg text-accent-400"></i>
                      <span className="text-sm">664 612 1065</span>
                    </a>
                    <a
                      href="#"
                      className="flex items-center gap-3 text-background-50/70 hover:text-background-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-instagram-line text-lg text-accent-400"></i>
                      <span className="text-sm">calma_mentoria_estrategica</span>
                    </a>
                  </div>

                  <div className="mt-10">
                    <Link
                      to="/agenda"
                      className="inline-block px-7 py-3.5 rounded-full text-sm font-medium tracking-[0.03em] whitespace-nowrap transition-all duration-300 cursor-pointer bg-background-50/15 border border-background-50/40 text-background-50 hover:bg-background-50/25"
                    >
                      Agenda una sesión inicial
                    </Link>
                  </div>
                </div>

                {/* Right: Contact Form */}
                <div className="lg:w-7/12">
                  <div className="bg-background-50 border border-background-200 rounded-xl p-8 md:p-10">
                    <h3 className="text-lg font-medium text-foreground-950 mb-8 tracking-[0.03em]">
                      Envía tu solicitud
                    </h3>

                    <form
                      ref={formRef}
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-5"
                    >
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="sr-only-custom"
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="contacto-nombre" className="block text-xs tracking-[0.08em] text-foreground-700 mb-2">
                            NOMBRE *
                          </label>
                          <input
                            type="text"
                            id="contacto-nombre"
                            name="nombre"
                            required
                            className="w-full bg-background-100 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                            placeholder="Tu nombre completo"
                          />
                        </div>
                        <div>
                          <label htmlFor="contacto-email" className="block text-xs tracking-[0.08em] text-foreground-700 mb-2">
                            CORREO *
                          </label>
                          <input
                            type="email"
                            id="contacto-email"
                            name="email"
                            required
                            className="w-full bg-background-100 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                            placeholder="tu@correo.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="contacto-telefono" className="block text-xs tracking-[0.08em] text-foreground-700 mb-2">
                          TELÉFONO (OPCIONAL)
                        </label>
                        <input
                          type="tel"
                          id="contacto-telefono"
                          name="telefono"
                          className="w-full bg-background-100 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                          placeholder="+52 000 000 0000"
                        />
                      </div>

                      <div>
                        <label htmlFor="contacto-cargo" className="block text-xs tracking-[0.08em] text-foreground-700 mb-2">
                          CARGO O ETAPA PROFESIONAL (OPCIONAL)
                        </label>
                        <input
                          type="text"
                          id="contacto-cargo"
                          name="cargo"
                          className="w-full bg-background-100 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
                          placeholder="Ej. Gerente de operaciones, recién promovido..."
                        />
                      </div>

                      <div>
                        <label htmlFor="contacto-mensaje" className="block text-xs tracking-[0.08em] text-foreground-700 mb-2">
                          ¿EN QUÉ TE GUSTARÍA TRABAJAR? *
                        </label>
                        <textarea
                          id="contacto-mensaje"
                          name="mensaje"
                          required
                          rows={4}
                          maxLength={500}
                          className="w-full bg-background-100 border border-background-300 rounded-lg px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors resize-none"
                          placeholder="Cuéntame un poco sobre tu momento actual y qué te gustaría trabajar en mentoría..."
                        ></textarea>
                        <p className="text-xs text-foreground-500 mt-1">Máximo 500 caracteres</p>
                      </div>

                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="contacto-consentimiento"
                          name="consentimiento"
                          required
                          className="mt-1 w-4 h-4 rounded border-background-300 bg-background-100 text-accent-500 focus:ring-accent-400/30 cursor-pointer"
                        />
                        <label htmlFor="contacto-consentimiento" className="text-xs text-foreground-700 leading-relaxed cursor-pointer">
                          Acepto el aviso de privacidad y el tratamiento de mis datos para recibir información sobre los servicios de mentoría. *
                        </label>
                      </div>

                      <button
                        type="submit"
                        disabled={formStatus === 'sending'}
                        className="w-full sm:w-auto px-8 py-3.5 rounded-full text-sm font-medium tracking-[0.03em] whitespace-nowrap transition-all duration-300 cursor-pointer bg-foreground-950 border border-foreground-950 text-background-50 hover:bg-foreground-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {formStatus === 'sending' ? 'Enviando...' : 'Enviar solicitud'}
                      </button>

                      {formStatus === 'success' && (
                        <div className="mt-4 p-4 rounded-lg bg-accent-100 border border-accent-300">
                          <p className="text-sm text-accent-800">
                            Gracias. Erika se pondrá en contacto contigo para conversar sobre tu proceso.
                          </p>
                        </div>
                      )}
                      {formStatus === 'error' && formError && (
                        <div className="mt-4 p-4 rounded-lg bg-red-100 border border-red-300">
                          <p className="text-sm text-red-700">{formError}</p>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>
      </main>
      <Footer />
    </>
  );
}